// 치과 환자 상세 /patients/:pid — 기본정보 · 보철물 제작 기록(읽기 전용, 편집 UI 없음 — 법무 §2-4 ③)
// · 상담·응대 메모(자유 텍스트 1칸, append-only, 정정은 새 메모 — 법무 §2-4 ①②)
// 상세 조회는 CF 경유(접속기록 — 계약 §3-6). 연결 해제(오너·직원, §3-9)·환자 삭제(오너만, §3-10) 버튼 포함.
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, User, ClipboardList, MessageSquare, Trash2, Unlink, CalendarClock, Link2 } from 'lucide-react';
import PageHeader from '../common/PageHeader';
import { unifiedStyles as U } from '../../styles/Styles';
import { getClinicPatientDetail, addPatientMemo, unlinkOrderFromPatient, deleteClinicPatient, DELETE_REASONS, describeError, deleteRequestReasonLabel, appRegisterBadgeLabel } from '../../services/clinicPatientsApi';
import { MEMO_LABEL, MEMO_NOT_RECORD, MEMO_MAX } from '../../config/patientConsent';
import ClinicPatientDocs from './ClinicPatientDocs';
import { effectiveNotify, effectiveDocs, isAcceptedConsentVersion } from '../../config/consentVersions';
import { getFeatures, isClinicRecallOn, isPatientFeedbackOn } from '../../utils/features';
import { useRoadmapPilot } from '../../utils/useRoadmapPilot';   // 🗺️ 진료 후 질문 노출 = 플래그 AND 파일럿 치과(서버 위탁 확인 isPilot·confirmed·isCurrent 1.2) (2026-10-06)
import { getOrderFeedbacks, describeFeedbackError, pointSummary, STATE_COLOR } from '../../services/clinicFeedbackApi';   // 🩺 장착 후 확인 요약 줄 (2026-10-02, 플래그 patientFeedback strict)
import RecallScheduleModal from './RecallScheduleModal';   // 🔔 [다음 안내 시기] — ClinicRecalls 와 같은 모달 (2026-10-01)
import { clinicSendBlockers, BLOCKER_SHORT } from '../../services/clinicRecallApi';   // 저장 응답 보류 플래그 → 짧은 사유 (5차)
import CareTrackStart from './CareTrackStart';   // 🗺️ [진료 끝 ▾]/[장착 완료] — 진료 후 질문 트랙 시작 (2026-10-06, 플래그 roadmapEngine strict)
import { hasAftercareConsent } from '../../services/careRoadmapApi';
import { useBookingAvailable } from '../../utils/useBookingAvailable';   // 📅 [본원 환자 확인] — 플래그 careBooking AND 서버 available 일 때만 (2026-10-11, 계획 W4)
import { markPatientVerified, describeBookingError, VERIFY_NOTE } from '../../services/bookingApi';
import { LinkOrderPickerModal } from './PatientOrderLink';   // 🦷 [의뢰서 연결] — 이미 저장된 의뢰서를 이 환자에게 연결 (2026-10-17 점검 P1-1)
import ClinicPatientNotices from './ClinicPatientNotices';   // 📨 「보낸 소식」 카드 — 조회 전용, 독립 로딩 (2026-10-06, 계약_치과_보낸소식_웹 §1)

const fmt = (v, withTime = false) => {
    if (!v) return '';
    const d = typeof v === 'number' ? new Date(v) : (v._seconds ? new Date(v._seconds * 1000) : (v.toDate ? v.toDate() : new Date(v)));
    if (Number.isNaN(d.getTime())) return '';
    const base = `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
    return withTime ? `${base} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}` : base;
};

// E.164(+8210…) → 010-1234-5678 표시. 국내 번호 외는 그대로
const fmtPhone = (e164) => {
    const d = String(e164 || '').replace(/[^0-9+]/g, '');
    const m = d.match(/^\+82(1[0-9])(\d{3,4})(\d{4})$/);
    if (m) return `0${m[1]}-${m[2]}-${m[3]}`;
    const k = d.match(/^0(1[0-9])(\d{3,4})(\d{4})$/);
    if (k) return `0${k[1]}-${k[2]}-${k[3]}`;
    return d;
};

function ClinicPatientDetail({ user }) {
    const { pid } = useParams();
    const navigate = useNavigate();
    const isOwner = !user?.companyId;

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [memo, setMemo] = useState('');
    const [memoBusy, setMemoBusy] = useState(false);
    const [memoError, setMemoError] = useState('');
    const [actionBusy, setActionBusy] = useState('');
    const [actionError, setActionError] = useState('');
    const [showDelete, setShowDelete] = useState(false);
    const [deleteReason, setDeleteReason] = useState('patient_request');
    const [showLinkOrder, setShowLinkOrder] = useState(false);   // [의뢰서 연결] 모달
    const [linkNotice, setLinkNotice] = useState('');
    // 🔔 재내원 안내 — 플래그 clinicRecall strict. [오늘 검진]·[장착 완료] 버튼은 서버 흐름(B1·S2) 없음 — 백로그, 만들지 않음
    const [recallOn, setRecallOn] = useState(false);
    const [showRecall, setShowRecall] = useState(false);
    const [recallNotice, setRecallNotice] = useState('');
    const [recallBlocked, setRecallBlocked] = useState(false);   // 저장 응답에 발송 보류 플래그가 있으면 안내를 주황색으로
    // 🩺 장착 후 확인 — 플래그 patientFeedback strict(베타 미리보기 없음). OFF면 CF 호출 0
    const [feedbackOn, setFeedbackOn] = useState(false);
    const [fbByOrder, setFbByOrder] = useState({});   // orderId → { points:[] } | { error }
    // 🗺️ 진료 후 질문(질문 로드맵) — 플래그 roadmapEngine strict AND 파일럿 치과(useRoadmapPilot, fail-closed). OFF면 버튼 비노출
    const { on: roadmapOn } = useRoadmapPilot(user);
    // 📅 [본원 환자 확인] (2026-10-11 계획 W4·B3) — 치과 찾기로만 등록한 케어 레코드는 치과가 확인해야 앱에서 예약 요청 가능(P1-3). OFF·파일럿 밖이면 버튼 0
    const { on: bookingOn } = useBookingAvailable(user);
    const [verifyLocal, setVerifyLocal] = useState(null);   // null = 서버 값 사용, true/false = 방금 누른 결과
    const [verifyBusy, setVerifyBusy] = useState(false);
    const [verifyError, setVerifyError] = useState('');
    useEffect(() => { setVerifyLocal(null); setVerifyError(''); }, [pid]);
    useEffect(() => {
        let alive = true;
        getFeatures().then(f => { if (alive) { setRecallOn(isClinicRecallOn(f, user, user?.email)); setFeedbackOn(isPatientFeedbackOn(f, user, user?.email)); } })
            .catch((e) => { console.error('[환자 상세] 플래그 조회 실패:', e?.message || e); if (alive) { setRecallOn(false); setFeedbackOn(false); } });
        return () => { alive = false; };
    }, [user]);

    const load = useCallback(async () => {
        setLoading(true); setError('');
        try { setData(await getClinicPatientDetail(pid)); }
        catch (e) { setError(describeError(e)); }
        finally { setLoading(false); }
    }, [pid]);

    useEffect(() => { load(); }, [load]);

    // 🩺 장착 후 확인 요약 — 연결 의뢰서 최근 10건(서버 cases linkedAt 내림차순) 전부 조회.
    //  도착 여부로 거르지 않는다: 납기 자정 자동 완료(auto_due)는 stageKey 'received'·arrivedAt null 이라 도착 필터에 빠졌음(검토 P1-6).
    //  시점이 없는 의뢰서는 서버가 points:[] 로 답하고 행에 아무것도 그리지 않는다.
    //  의뢰서마다 getOrderFeedbacks 1회 = 접속기록 feedback_view 1건(계약 B-6). 실패는 행에 표시(조용한 실패 금지)
    useEffect(() => {
        if (!feedbackOn || !data) { setFbByOrder({}); return; }
        const targets = (data.cases || []).filter(c => !c.unlinked && c.orderId).slice(0, 10);
        if (targets.length === 0) { setFbByOrder({}); return; }
        let alive = true;
        (async () => {
            const entries = await Promise.all(targets.map(async (c) => {
                try { const r = await getOrderFeedbacks(c.orderId); return [c.orderId, { points: Array.isArray(r?.points) ? r.points : [] }]; }
                catch (e) { return [c.orderId, { error: describeFeedbackError(e, '장착 후 확인을 불러오지 못했습니다.') }]; }
            }));
            if (alive) setFbByOrder(Object.fromEntries(entries));
        })();
        return () => { alive = false; };
    }, [feedbackOn, data]);

    const submitMemo = async () => {
        const text = memo.trim();
        if (!text) { setMemoError('메모 내용을 입력해 주세요.'); return; }
        if (text.length > MEMO_MAX) { setMemoError(`메모는 ${MEMO_MAX}자 이내로 입력해 주세요.`); return; }
        setMemoBusy(true); setMemoError('');
        try {
            await addPatientMemo(pid, text);
            setMemo('');
            await load();
        } catch (e) {
            setMemoError(describeError(e));
        } finally { setMemoBusy(false); }
    };

    // 연결 해제 — cases.unlinked=true(이력 보존) + workOrders 2키 제거
    const unlink = async (c) => {
        if (!window.confirm('이 의뢰서와 환자 연결을 해제할까요? 기록은 해제 표시로 남습니다.')) return;
        setActionBusy(c.orderId); setActionError('');
        try { await unlinkOrderFromPatient(c.orderId, pid); await load(); }
        catch (e) { setActionError(describeError(e)); }
        finally { setActionBusy(''); }
    };

    // 환자 삭제 — 오너만. 사유는 enum 선택(서버 §0-0 P2-6). 식별정보 비움 + 하위 삭제(서버). 되돌릴 수 없음
    const remove = async () => {
        if (!isOwner) return;
        setActionBusy('delete'); setActionError('');
        try { await deleteClinicPatient(pid, deleteReason); navigate('/patients'); }
        catch (e) { setActionError(describeError(e)); setActionBusy(''); }
    };

    // 환자 측 삭제 요청 건 처리 — 오너만. 사유는 'patient_request' 고정(환자 요청에 따른 파기, 계약 §3-15). 되돌릴 수 없음
    const processDeleteRequest = async () => {
        if (!isOwner) return;
        if (!window.confirm('환자가 요청한 삭제를 처리할까요? 등록 정보·제작 기록·메모가 모두 지워지며 되돌릴 수 없습니다.')) return;
        setActionBusy('delete'); setActionError('');
        try { await deleteClinicPatient(pid, 'patient_request'); navigate('/patients'); }
        catch (e) { setActionError(describeError(e)); setActionBusy(''); }
    };

    const p = data?.record || {};
    const isDeleteRequested = p.status === 'deleteRequested';
    const isUnconfirmed = p.status === 'unconfirmed';
    // 치과 확인 여부 — 서버 상세 응답 clinicVerifiedAt(계획 S3 markPatientVerified 기록 필드). 방금 누른 결과가 있으면 그것 우선
    const clinicVerified = verifyLocal !== null ? verifyLocal : !!p.clinicVerifiedAt;
    const showVerify = bookingOn && p.source === 'care' && p.registeredVia === 'care_search' && p.status === 'active';
    const doVerify = async (clear) => {
        if (clear && !window.confirm('치과 확인을 해제할까요? 해제하면 이 환자는 앱에서 예약 요청을 할 수 없습니다.')) return;
        setVerifyBusy(true); setVerifyError('');
        try { const r = await markPatientVerified(pid, { clear }); setVerifyLocal(typeof r?.verified === 'boolean' ? r.verified : !clear); }
        catch (e) { setVerifyError(describeBookingError(e)); }
        finally { setVerifyBusy(false); }
    };   // 앱 자가등록 확인 전(계약 v2 §A-1) — 번호 필드 없음
    const phoneMissing = p.phonePending === true || (!p.phoneLast4 && !p.phoneE164);   // 확인 시 Auth 번호가 없어 미기록(phonePending) 또는 번호 자체 없음
    const cases = (data?.cases || []).filter(c => !c.unlinked);
    const unlinkedCases = (data?.cases || []).filter(c => c.unlinked);
    const memos = data?.memos || [];

    // 동의·알림·서류 표시 (2026-09-16 환자기능확장) — 서버 값(notify·docs) 우선, 구 서버 응답은 같은 규칙으로 판정
    const consentVer = p.consents?.sensitive?.version || p.consents?.collect?.version || '';
    const consentText = p.consents?.collect?.agreed
        ? `수집·이용 / 민감정보${p.docsPref ? '(서류 사본 전달 포함)' : ''} / 위탁 고지 · 버전 ${consentVer}${consentVer && !isAcceptedConsentVersion(consentVer) ? '(확인 필요)' : ''} · ${fmt(p.consents.collect.at)}${p.consents.collect.channel ? ` · ${p.consents.collect.channel}` : ''}${p.consentMethod === 'required_all' ? ' · 필수 항목 모두 동의' : p.consentMethod === 'individual' ? ' · 항목별 동의' : ''}`
        : '기록 없음';
    const notifyOn = typeof p.notify === 'boolean' ? p.notify : effectiveNotify(p);
    const notifyText = p.notifyPref
        ? (notifyOn ? '켜짐 (등록 시 기본 안내)' : '꺼짐 (환자가 끔)')
        : (notifyOn ? '켜짐 (환자 동의)' : '꺼짐 (동의하지 않음 또는 철회)');
    // 덴클 케어(앱 등록) 레코드 — 서버 상세 응답 source 'care'(clinic-patients.js isCareRecord 와 같은 기준). 케어 레코드는 서류 기능이 없다(서버 서류 CF 가 케어 레코드 거부)
    const isCare = p.source === 'care';
    // 연결 가능 = active + 필수 동의(collect·sensitive·delegation) — 서버 hasLinkConsents·명부 선택 모달 consentOk 와 같은 조건(최종 판정은 서버)
    const canLinkOrder = p.status === 'active' && !!(p.consents?.collect?.agreed && p.consents?.sensitive?.agreed && p.consents?.delegation?.agreed);
    // 케어 선택 동의 3종(진료 후 질문 aftercare · 재내원 안내 recall · 예약 요청 booking) — 서버 상세는 consents 전 키를 {agreed, version, at, channel}로 내려준다.
    //   철회도 agreed:false 로 기록되므로(care-roadmap.js aftercareRevocationPatch · clinic-recall.js 끄기 · care-booking.js setCareBookingOptIn) agreed 로 판정. 기록 자체가 없으면 「동의 없음」
    const optConsentText = (c) => {
        if (!c) return '동의 없음';
        if (c.agreed) return `동의함${c.at ? ` · ${fmt(c.at)}` : ''}`;
        return `꺼짐 (환자가 끔)${c.at ? ` · ${fmt(c.at)}` : ''}`;
    };
    const docsOn = typeof p.docs === 'boolean' ? p.docs : effectiveDocs(p);
    const docsText = p.docsPref
        ? (docsOn ? '켜짐 (민감정보 처리 동의에 포함)' : '꺼짐 (환자가 끔)')
        : (docsOn ? '켜짐 (환자 선택 동의)' : '꺼짐 (선택 동의 없음)');

    const row = (label, value) => (
        <div style={{ display: 'flex', gap: 12, padding: '6px 0', borderBottom: '1px solid #f1f5f9', fontSize: 13 }}>
            <div style={{ width: 90, color: '#64748b', flexShrink: 0 }}>{label}</div>
            <div style={{ color: '#0f172a' }}>{value || '-'}</div>
        </div>
    );

    return (
        <div style={U.content}>
            <PageHeader icon={User} title={p.name ? `${p.name} 환자` : '환자 상세'} subtitle="등록 정보 · 보철물 제작 기록 · 상담·응대 메모"
                actions={<button type="button" onClick={() => navigate('/patients')} style={U.buttonSecondary}><ChevronLeft size={14} /> 목록</button>} />

            {error && <div style={{ background: '#fee2e2', color: '#b91c1c', borderRadius: 8, padding: '8px 12px', fontSize: 13, marginBottom: 10 }}>{error} <button type="button" onClick={load} style={{ ...U.buttonSecondary, padding: '2px 8px', marginLeft: 6 }}>다시 시도</button></div>}
            {loading && <div style={{ ...U.emptyState, padding: '40px 0' }}>불러오는 중...</div>}

            {showRecall && (
                <RecallScheduleModal patientId={pid} patientName={p.name} onClose={() => setShowRecall(false)}
                    onSaved={(res) => {
                        // 계약 B-1(5차): 저장 응답 clinicPhoneMissing·clinicNameMissing·clinicNameBanned → 예정일 뒤에 보류 사유 병기
                        const base = res?.dueDay ? `안내 예정일 ${String(res.dueDay).replace(/-/g, '.')} 저장됨` : '저장됨';
                        const b = clinicSendBlockers(res).map(k => BLOCKER_SHORT[k]);
                        setRecallBlocked(b.length > 0);
                        setRecallNotice(b.length ? `${base} · ${b.join(' · ')}` : base);
                    }} />
            )}
            {showLinkOrder && (
                <LinkOrderPickerModal clinicId={user?.companyId || user?.uid} record={{ ...p, pid }}
                    onClose={() => setShowLinkOrder(false)}
                    onLinked={async (r) => {
                        setShowLinkOrder(false);
                        setLinkNotice(r?.already ? '이미 연결된 의뢰서입니다.' : '의뢰서를 연결했습니다.');
                        await load();
                    }} />
            )}
            {showDelete && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
                    <div style={{ ...U.card, width: '100%', maxWidth: 420 }}>
                        <h3 style={{ margin: '0 0 8px', fontSize: 15, fontWeight: 700, color: '#0f172a' }}>환자 삭제</h3>
                        <div style={{ fontSize: 13, color: '#334155', marginBottom: 10 }}>등록 정보·제작 기록·메모가 모두 지워지며 되돌릴 수 없습니다. 삭제 사유를 선택해 주세요.</div>
                        {DELETE_REASONS.map(r => (
                            <label key={r.value} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', fontSize: 13, color: '#0f172a', cursor: 'pointer' }}>
                                <input type="radio" name="deleteReason" value={r.value} checked={deleteReason === r.value} onChange={() => setDeleteReason(r.value)} />
                                {r.label}
                            </label>
                        ))}
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
                            <button type="button" disabled={actionBusy === 'delete'} onClick={() => setShowDelete(false)} style={U.buttonSecondary}>취소</button>
                            <button type="button" disabled={actionBusy === 'delete'} onClick={remove}
                                style={{ ...U.buttonPrimary, background: '#dc2626' }}><Trash2 size={14} /> {actionBusy === 'delete' ? '삭제 중...' : '삭제'}</button>
                        </div>
                    </div>
                </div>
            )}

            {!loading && data && (
                <>
                    <div style={{ ...U.card, marginBottom: 12 }}>
                        <h3 style={{ margin: '0 0 8px', fontSize: 15, fontWeight: 700, color: '#0f172a' }}>기본 정보</h3>
                        {isDeleteRequested && (
                            <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 8, padding: '8px 12px', marginBottom: 8, fontSize: 13, color: '#9a3412' }}>
                                <span style={{ ...U.badge, background: '#ffedd5', color: '#9a3412', marginRight: 6 }}>{'삭제 요청됨 · 10일 내 처리'}</span>
                                사유: {deleteRequestReasonLabel(p.deleteRequestReason)}{p.deleteRequestedAt ? ` · 요청일 ${fmt(p.deleteRequestedAt)}` : ''}
                                <div style={{ marginTop: 4, fontSize: 12 }}>{'요청일부터 10일이 지나면 자동 삭제됩니다.'}</div>
                            </div>
                        )}
                        {row('이름', p.name)}
                        {/* 연락처 전체 표시 (2026-10-01 대표 지시 "연락처 정도는 남아서 보게") — 상세는 CF 경유·접속기록 남음(계약 §3-6). 목록은 뒤 4자리 유지(일괄 노출 면적 최소화). 서버는 원래 phoneE164를 내려주고 있었음 */}
                        {row('연락처', p.phoneE164 ? fmtPhone(p.phoneE164) : p.phoneLast4 ? `***-${p.phoneLast4}` : (p.phoneMasked || ''))}
                        {row('생년월일', p.birthDate || (p.birthYear ? `${p.birthYear}년` : ''))}
                        {row('성별', p.gender === 'F' ? '여' : p.gender === 'M' ? '남' : '')}
                        {row('요청사항', p.note)}
                        {/* 덴클 케어 앱 등록(source 'care')은 원문 대신 「앱 등록(QR)/(찾기)」 — 목록 배지와 같은 appRegisterBadgeLabel(정본 L-4) */}
                        {row('출처', p.source === 'care' ? appRegisterBadgeLabel(p) : p.source === 'qr' ? 'QR 등록' : p.source === 'favorite' ? '환자앱 단골' : p.source === 'clinic_manual' ? '수기' : p.source === 'self' ? '앱 등록' : p.source)}
                        {row('동의', consentText)}
                        {p.consents?.collect?.agreed && row('앱 알림', notifyText)}
                        {/* 케어(앱 등록) 레코드는 서류 기능 자체가 없다 — 「서류 사본」 줄 대신 고정 안내 (2026-10-17 점검 P1-2) */}
                        {p.consents?.collect?.agreed && row('서류 사본', isCare ? '앱 등록 환자 — 서류 없음' : docsText)}
                        {/* 케어 선택 동의 3종 (2026-10-17 점검 P2) — 앱 등록 환자이거나 기록이 있을 때만 */}
                        {(isCare || p.consents?.aftercare) && row('진료 후 질문', optConsentText(p.consents?.aftercare))}
                        {(isCare || p.consents?.recall) && row('재내원 안내', optConsentText(p.consents?.recall))}
                        {/* 예약 요청은 careBooking 이 열린(bookingOn) 치과에서만 기본 표시 — 꺼진 치과에서 모든 환자 「동의 없음」은 오해 소지. 기록이 있으면 항상 */}
                        {((isCare && bookingOn) || p.consents?.booking) && row('예약 요청', optConsentText(p.consents?.booking))}
                        {row('번호 확인', p.phoneVerifiedBy === 'kakao_account' ? '카카오 계정' : p.phoneVerifiedBy ? '휴대폰 문자 인증' : '미인증')}
                        {row('상태', isDeleteRequested ? '환자가 삭제 요청함 — 대표가 10일 내 [삭제 처리] (요청일부터 10일이 지나면 자동 삭제됩니다)' : p.status === 'deleted' ? '삭제됨' : isUnconfirmed ? '이전 방식 등록 — 등록일부터 30일이 지나면 자동으로 삭제됩니다' : phoneMissing ? '번호 확인 전' : '정상')}
                        {isDeleteRequested && row('삭제 요청', `${deleteRequestReasonLabel(p.deleteRequestReason)}${p.deleteRequestedAt ? ` · ${fmt(p.deleteRequestedAt, true)}` : ''}`)}
                        {row('환자앱', p.fcmRegistered ? '설치됨(도착 알림 가능)' : '미설치(알림 없음 — 직접 안내)')}
                        {row('등록일', fmt(p.createdAt))}
                        {showVerify && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
                                {clinicVerified ? (
                                    <>
                                        <span style={{ ...U.badge, background: '#eef2ff', color: '#4338ca' }}>{'치과 확인'}</span>
                                        {isOwner && <button type="button" disabled={verifyBusy} onClick={() => doVerify(true)} style={U.buttonSecondary}>{verifyBusy ? '처리 중...' : '확인 해제'}</button>}
                                    </>
                                ) : (
                                    <>
                                        <button type="button" disabled={verifyBusy} onClick={() => doVerify(false)} style={U.buttonSecondary}>{verifyBusy ? '처리 중...' : '본원 환자 확인'}</button>
                                        <span style={{ fontSize: 12, color: '#94a3b8' }}>{VERIFY_NOTE}</span>
                                    </>
                                )}
                                {verifyError && <span style={{ fontSize: 12, color: '#b91c1c' }}>{verifyError}</span>}
                            </div>
                        )}
                        {recallOn && p.status !== 'deleted' && !isUnconfirmed && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
                                <button type="button" onClick={() => setShowRecall(true)} style={U.buttonSecondary}><CalendarClock size={14} /> 다음 안내 시기</button>
                                <span style={{ fontSize: 12, color: '#94a3b8' }}>치과 명의 재내원 안내(앱 설치·동의 환자만). 목록은 「재내원 안내」 메뉴.</span>
                                {recallNotice && <span style={{ fontSize: 12, color: recallBlocked ? '#b45309' : '#047857' }}>{recallNotice}</span>}
                            </div>
                        )}
                        {/* 🗺️ [진료 끝 ▾] — 진료 유형 1클릭(보철 4개 = [장착 완료], 기준일 기본 오늘). 동의 없는 환자는 비활성 + T-17. 진행 중 일정 표시·[취소]는 대표만 (계약 §2) */}
                        {roadmapOn && p.status === 'active' && !isUnconfirmed && (
                            <CareTrackStart pid={pid} name={p.name} consentOn={hasAftercareConsent(p)} isOwner={isOwner} />
                        )}
                        {actionError && <div style={{ background: '#fee2e2', color: '#b91c1c', borderRadius: 8, padding: '8px 12px', fontSize: 13, marginTop: 8 }}>{actionError}</div>}
                        {isOwner ? (
                            isDeleteRequested ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
                                    <button type="button" disabled={actionBusy === 'delete'} onClick={processDeleteRequest}
                                        style={{ ...U.buttonPrimary, background: '#dc2626' }}><Trash2 size={14} /> {actionBusy === 'delete' ? '삭제 중...' : '삭제 처리'}</button>
                                    <span style={{ fontSize: 12, color: '#94a3b8' }}>환자 요청에 따라 식별정보·제작 기록·메모를 지웁니다. 대표만 가능.</span>
                                </div>
                            ) : p.status !== 'deleted' && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
                                    <button type="button" disabled={actionBusy === 'delete'} onClick={() => setShowDelete(true)}
                                        style={{ ...U.buttonSecondary, color: '#b91c1c', borderColor: '#fecaca', background: '#fff' }}><Trash2 size={14} /> 환자 삭제</button>
                                    <span style={{ fontSize: 12, color: '#94a3b8' }}>식별정보·제작 기록·메모가 모두 지워집니다. 대표만 가능.</span>
                                </div>
                            )
                        ) : (
                            <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 8 }}>환자 레코드 삭제는 치과 대표만 할 수 있습니다.</div>
                        )}
                    </div>

                    {/* 보철물 제작 기록 — 읽기 전용. 편집·추가·삭제 UI를 두지 않는다 (법무 §2-4 ③) */}
                    <div style={{ ...U.card, marginBottom: 12 }}>
                        <h3 style={{ margin: '0 0 8px', fontSize: 15, fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 6 }}><ClipboardList size={16} color="#6366f1" /> 보철물 제작 기록</h3>
                        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>의뢰서를 기준으로 자동 표시됩니다. 여기서 수정할 수 없습니다.</div>
                        {/* 🦷 [의뢰서 연결] — 이미 저장된 의뢰서(앱 등록 전에 보낸 것·연결 실패한 것·덴클 앱으로 쓴 것)를 이 환자에게 (2026-10-17 점검 P1-1) */}
                        {canLinkOrder && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
                                <button type="button" onClick={() => { setLinkNotice(''); setShowLinkOrder(true); }} style={{ ...U.buttonSecondary, padding: '4px 10px', fontSize: 12 }}><Link2 size={12} /> {'의뢰서 연결'}</button>
                                <span style={{ fontSize: 12, color: '#94a3b8' }}>{'이미 보낸 의뢰서 중 이 환자 것을 골라 연결합니다.'}</span>
                                {linkNotice && <span style={{ fontSize: 12, color: '#047857' }}>{linkNotice}</span>}
                            </div>
                        )}
                        {cases.length === 0 ? (
                            <div style={{ fontSize: 13, color: '#94a3b8' }}>{canLinkOrder ? '연결된 의뢰서가 없습니다. 위 [의뢰서 연결]로 이미 보낸 의뢰서를 연결하거나, 의뢰서 작성 화면에서 [명부에서 선택]으로 연결하세요.' : '연결된 의뢰서가 없습니다.'}</div>
                        ) : cases.map(c => (
                            <div key={c.orderId} style={{ borderTop: '1px solid #f1f5f9', padding: '8px 0', fontSize: 13 }}>
                                <div style={{ fontWeight: 600, color: '#0f172a' }}>{(c.prosthesis || []).join(', ') || '보철물'}{(c.material || []).length ? ` · ${c.material.join(', ')}` : ''}</div>
                                <div style={{ color: '#64748b' }}>
                                    {(c.teeth || []).length > 0 && `치아 ${c.teeth.join(', ')} · `}
                                    의뢰 {fmt(c.orderedAt)}{c.packedAt ? ` · 완성 ${fmt(c.packedAt)}` : ''}{c.arrivedAt ? ` · 도착 ${fmt(c.arrivedAt)}` : ''}
                                    {/* 단계 라벨은 서버값(stageLabel·packedLabel) 그대로 — 웹 재판정 금지 */}
                                    {' · '}<span style={{ ...U.badge, background: c.stageKey === 'arrived' ? '#d1fae5' : '#eef2ff', color: c.stageKey === 'arrived' ? '#047857' : '#4338ca' }}>{c.stageLabel || c.stageKey || ''}</span>
                                    {c.packed && <span style={{ ...U.badge, background: '#fef3c7', color: '#b45309', marginLeft: 4 }}>{c.packedLabel || '완성'}</span>}
                                </div>
                                {/* 🩺 장착 후 확인 요약 1줄 — "장착 후 확인 · 3일 확인 필요(46 임플란트) · 2주 예정". 답 내용은 의뢰서 보기 카드에서만 */}
                                {feedbackOn && fbByOrder[c.orderId] && (fbByOrder[c.orderId].error || fbByOrder[c.orderId].points.length > 0) && (
                                    <div style={{ fontSize: 12.5, marginTop: 3, color: '#475569' }}>
                                        <span style={{ fontWeight: 600 }}>장착 후 확인</span>
                                        {fbByOrder[c.orderId].error
                                            ? <span style={{ color: '#b91c1c' }}>{` · ${fbByOrder[c.orderId].error}`}</span>
                                            : fbByOrder[c.orderId].points.map(pt => {
                                                const sm = pointSummary(pt);
                                                return <span key={pt.fbId || pt.point} style={{ color: STATE_COLOR[sm.color].fg, fontWeight: sm.color === 'red' ? 700 : 400 }}>{` · ${sm.text}`}</span>;
                                            })}
                                    </div>
                                )}
                                <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                                    {c.orderId && <button type="button" onClick={() => navigate(`/view-order/${c.orderId}`)} style={{ ...U.buttonSecondary, padding: '3px 10px', fontSize: 12 }}>의뢰서 보기</button>}
                                    <button type="button" disabled={actionBusy === c.orderId} onClick={() => unlink(c)} style={{ ...U.buttonSecondary, padding: '3px 10px', fontSize: 12 }}><Unlink size={12} /> {actionBusy === c.orderId ? '해제 중...' : '연결 해제'}</button>
                                </div>
                            </div>
                        ))}
                        {unlinkedCases.length > 0 && (
                            <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 8 }}>연결 해제된 기록 {unlinkedCases.length}건 (이력 보존, 환자 화면에는 표시되지 않음)</div>
                        )}
                    </div>

                    {/* 📨 보낸 소식 — 제작 기록 바로 아래·서류함 위(기획 §6-1). 환자 관리가 열린 치과 전부(대표·직원), 소스별 플래그와 무관. 앵커 #notices */}
                    <ClinicPatientNotices pid={pid} />

                    {/* 서류함 (P2-12) — 치과 발급 서류 사본 올리기·열기·삭제. 상세 응답에 docs[]가 있으면 그대로, 없으면 카드가 목록 CF 호출 */}
                    {/* 케어(앱 등록) 레코드는 서류를 보낼 수 없어(서버 케어 제외) 카드 자체를 숨긴다 — 동의만 받으면 될 것처럼 보이던 문제(2026-10-17 점검 P1-2·P2) */}
                    {!isCare && <ClinicPatientDocs pid={pid} record={p} user={user} isOwner={isOwner} initialDocs={Array.isArray(data?.docs) ? data.docs : undefined} />}

                    {/* 상담·응대 메모 — 자유 텍스트 1칸만. 진단명 선택·처치 코드·치식·태그 없음 (법무 §2-4 ②) */}
                    <div style={U.card}>
                        <h3 style={{ margin: '0 0 8px', fontSize: 15, fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 6 }}><MessageSquare size={16} color="#6366f1" /> {MEMO_LABEL}</h3>
                        {memoError && <div style={{ background: '#fee2e2', color: '#b91c1c', borderRadius: 8, padding: '8px 12px', fontSize: 13, marginBottom: 8 }}>{memoError}</div>}
                        <textarea value={memo} onChange={(e) => setMemo(e.target.value)} maxLength={MEMO_MAX} rows={3}
                            placeholder="예: 다음 내원 시 보철 색상 상의 원함"
                            style={{ width: '100%', boxSizing: 'border-box', padding: 10, fontSize: 13, border: '1px solid #e2e8f0', borderRadius: 8, resize: 'vertical', fontFamily: 'inherit' }} />
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
                            <span style={{ fontSize: 12, color: '#94a3b8' }}>{memo.length}/{MEMO_MAX}</span>
                            <button type="button" disabled={memoBusy} onClick={submitMemo} style={U.buttonPrimary}>{memoBusy ? '저장 중...' : '메모 추가'}</button>
                        </div>
                        {/* 고정 문구 — 문구·위치 변경 금지 (법무 §2-4 ①) */}
                        <div style={{ fontSize: 12, color: '#b45309', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '8px 10px', marginTop: 8, lineHeight: 1.5 }}>
                            {MEMO_NOT_RECORD}
                        </div>
                        <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 6 }}>메모는 수정·삭제하지 않고 쌓입니다. 정정할 내용은 새 메모로 남겨 주세요.</div>

                        <div style={{ marginTop: 12 }}>
                            {memos.length === 0 ? (
                                <div style={{ fontSize: 13, color: '#94a3b8' }}>아직 메모가 없습니다.</div>
                            ) : memos.map(m => (
                                <div key={m.id} style={{ borderTop: '1px solid #f1f5f9', padding: '8px 0', fontSize: 13 }}>
                                    <div style={{ color: '#0f172a', whiteSpace: 'pre-wrap' }}>{m.text}</div>
                                    <div style={{ color: '#94a3b8', fontSize: 12, marginTop: 2 }}>{m.createdBy === user?.uid ? '나' : (m.createdBy ? `직원 ${String(m.createdBy).slice(0, 6)}` : '')} · {fmt(m.createdAt, true)}{m.supersedes ? ' · 정정' : ''}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

export default ClinicPatientDetail;
